import type { AxiosError } from "axios";
import { UNKNOWN_JOB_NAME } from "./forms/jobGroups";

/** i18n key the backend raises with a 409 when a de-enabled job is still in use (DAT-464). */
export const JOB_REMOVAL_CONFLICT_KEY = "department.job.remove.in.use";

/** One active employee blocking a job's removal. */
export type ConflictingEmployee = {
	nurseId: number;
	name: string;
	/** True when they hold another job besides this one — the PRD wants these listed separately. */
	hasOtherJobs: boolean;
};

/** One blocking scheduled shift. `nurseName` is null when the shift carries no nurse. */
export type ConflictingScheduledShift = {
	shiftId: string;
	nurseName: string | null;
	/** ISO datetimes — rendered in the department's timezone by the label formatters. */
	startTime: string;
	endTime: string | null;
	location: string | null;
	shiftType: string | null;
};

/**
 * `recurringParams` is polymorphic, matching the backend's `BaseRecurring` subclasses: `Weekly`
 * carries `weekdays` + `interval`, `ConsecutiveDays` carries the work/rest lengths, `Daily` carries
 * neither. Read defensively — it comes off a JSONB column.
 */
export type ConflictingRecurringParams = {
	recurring_type?: string;
	recurringType?: string;
	weekdays?: string[];
	interval?: number;
	working_days_amount?: number;
	rest_days_amount?: number;
};

/** One blocking recurring shift. Times are clock times ("HH:MM:SS"), not datetimes. */
export type ConflictingRecurringShift = {
	recurringShiftId: number;
	nurseName: string | null;
	shiftStart: string | null;
	shiftEnd: string | null;
	location: string | null;
	endDate: string | null;
	recurringParams: ConflictingRecurringParams | null;
};

/** One blocking Schedule Template slot. */
export type ConflictingTemplateLocation = {
	templateId: string;
	location: string | null;
	locationShortName: string;
	section: string;
	days: string[];
	/** Clock times stored as text on `shift_template`, e.g. "07:00". */
	shiftStart: string;
	shiftEnd: string;
	shiftType: string;
};

/** One blocking posted open shift. */
export type ConflictingOpenShift = {
	requestId: string;
	shiftStart: string;
	shiftEnd: string;
	shiftType: string | null;
	subRole: string | null;
	location: string | null;
	neededNurses: number | null;
	agencySourceName: string | null;
};

/**
 * Everything that would be stranded by de-enabling one job. Mirrors `get_job_removal_conflicts`.
 *
 * Each category has both a count and an `…Items` list (DAT-805). The **count is authoritative**; the
 * list is a sample capped server-side at `JOB_CONFLICT_ITEM_LIMIT`, soonest-first, because one job can
 * hold five figures' worth of shifts. The item arrays are optional so a frontend deployed ahead of the
 * backend degrades to DAT-464's counts rather than crashing.
 */
export type JobRemovalConflictDetail = {
	activeEmployees: ConflictingEmployee[];
	scheduledShifts: number;
	recurringShifts: number;
	templateLocations: number;
	openShifts: number;
	scheduledShiftItems?: ConflictingScheduledShift[];
	recurringShiftItems?: ConflictingRecurringShift[];
	templateLocationItems?: ConflictingTemplateLocation[];
	openShiftItems?: ConflictingOpenShift[];
};

/** Keyed by job id — a single save can de-enable several jobs at once. */
export type JobRemovalConflicts = Record<string, JobRemovalConflictDetail>;

/**
 * FastAPI nests an `HTTPException` payload under `detail`, so the body is
 * `{ detail: { message, error_type, details } }` — the same idiom `usePbjAudit` and `AddDayOffDialog`
 * read. `detail` can also be a plain string for errors raised without a dict, hence the union.
 */
type ConflictResponseBody = {
	detail?:
		| {
				message?: string;
				error_type?: string;
				details?: { conflicts?: JobRemovalConflicts };
		  }
		| string;
};

/**
 * Carries the conflict payload through the save pipeline.
 *
 * The department-config save wraps every failure in a generic `Error` and the outer handler renders
 * `detail` into a snackbar — which for this error would stringify an object and lose the list Ops needs.
 * A dedicated error type lets the save site recognise it and open the dialog instead.
 */
export class JobRemovalConflictError extends Error {
	readonly conflicts: JobRemovalConflicts;

	constructor(conflicts: JobRemovalConflicts) {
		super(JOB_REMOVAL_CONFLICT_KEY);
		this.name = "JobRemovalConflictError";
		this.conflicts = conflicts;
	}
}

const isConflictShape = (value: unknown): value is JobRemovalConflicts =>
	typeof value === "object" &&
	value !== null &&
	Object.values(value).every((detail) => typeof detail === "object" && detail !== null && "activeEmployees" in detail);

/**
 * Pulls the conflict payload out of a failed save, unwrapping the generic `Error` the save pipeline
 * wraps around the original Axios error. Returns null for anything that isn't this specific 409, so
 * every other failure keeps its existing snackbar path.
 */
export function parseJobRemovalConflict(error: unknown): JobRemovalConflicts | null {
	if (error instanceof JobRemovalConflictError) return error.conflicts;

	const axiosError = (error instanceof Error && (error.cause as AxiosError<ConflictResponseBody> | undefined)) || (error as AxiosError<ConflictResponseBody>);
	if (axiosError?.response?.status !== 409) return null;

	const detail = axiosError.response.data?.detail;
	if (typeof detail !== "object" || detail === null || detail.message !== JOB_REMOVAL_CONFLICT_KEY) return null;

	const conflicts = detail.details?.conflicts;
	return isConflictShape(conflicts) ? conflicts : null;
}

/** True when the detail has anything worth showing — guards against an empty payload. */
export const hasAnyConflict = (detail: JobRemovalConflictDetail): boolean =>
	detail.activeEmployees.length > 0 || detail.scheduledShifts > 0 || detail.recurringShifts > 0 || detail.templateLocations > 0 || detail.openShifts > 0;

/** The catalog shape the naming helpers below read — `useAllJobs`'s rows, narrowed to what they need. */
type NameableJob = { id: string; name: string; displayName: string };

/**
 * id → displayName for the jobs this dialog may NAME, the one source both the title and the per-group
 * prefixes read — built once so the two can never disagree about what a job is called.
 *
 * The `unknown` sentinel is left out because every picker hides it, so a user has never seen it; an id
 * missing from the catalog is simply absent, so the caller falls back to a label instead of a raw UUID.
 */
export function nameableJobNameById(jobs: NameableJob[]): Map<string, string> {
	return new Map(jobs.filter((job) => job.name !== UNKNOWN_JOB_NAME).map((job) => [job.id, job.displayName]));
}

/**
 * What one conflicting job is called wherever it has to be named individually (the group prefixes).
 * Falls back to the already-translated `unnamedLabel` — a job the dialog cannot name still needs to
 * read as a distinct job, or a two-job refusal looks like one job listed twice.
 */
export function conflictJobLabel(jobId: string, jobNames: Map<string, string>, unnamedLabel: string): string {
	return jobNames.get(jobId) ?? unnamedLabel;
}

/**
 * The conflicting jobs this dialog can actually NAME, in payload order.
 *
 * Excludes the `unknown` sentinel (every picker hides it, so a user has never seen it) and any id
 * missing from the catalog (the alternative was printing a raw UUID). Both produced a refusal titled
 * after something the reader could not recognise — "Unknown is still in use". An empty result means
 * the caller should use the generic title; the conflicts themselves are still listed in full.
 */
export function nameableConflictJobNames(conflictJobIds: string[], jobs: NameableJob[]): string[] {
	const byId = nameableJobNameById(jobs);
	return conflictJobIds.map((jobId) => byId.get(jobId)).filter((name): name is string => name !== undefined);
}
